import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    const safeValue = Number.isFinite(value) ? value : 0;
    // Keep Horizon’s "100+" style behavior (blank when 100+)
    this.refs.cartBubbleCount.textContent = safeValue < 100 ? String(safeValue) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    // Always sync from Shopify on load (prevents stale mobile counts)
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * Mobile browsers love restoring old DOM state; we re-sync from Shopify.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (_event) => {
    this.ensureCartBubbleIsCorrect();
  };

  /**
   * Fetches the real cart count from Shopify.
   * @returns {Promise<number>}
   */
  fetchCartItemCount = async () => {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    const cart = await res.json();
    return Number.isFinite(cart?.item_count) ? cart.item_count : 0;
  };

  /**
   * Handles the cart update event.
   * IMPORTANT: Different sources dispatch different meanings for itemCount (delta vs total).
   * We ignore the payload and always read the real cart total from /cart.js.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (_event) => {
    try {
      const itemCount = await this.fetchCartItemCount();
      this.renderCartBubble(itemCount, false, true);
    } catch (_) {
      // If fetch fails, do nothing rather than showing a wrong number
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - Absolute total items in cart.
   * @param {boolean} _comingFromProductForm - unused; kept for signature compatibility.
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, _comingFromProductForm, animate = true) => {
    const nextCount = Number.isFinite(itemCount) ? itemCount : 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);

    this.currentCartCount = nextCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', this.currentCartCount > 0);

    // Keep sessionStorage write for Horizon behavior, but we no longer trust it as truth.
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || nextCount === 0) return;

    // Ensure element is visible before starting animation
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Ensures cart bubble matches Shopify's real cart count.
   * This fixes mobile/session restore weirdness and prevents drift.
   */
  ensureCartBubbleIsCorrect = async () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    try {
      const itemCount = await this.fetchCartItemCount();
      // Set absolute count, no animation
      this.renderCartBubble(itemCount, false, false);
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
